<template>
  <div class="space-y-3">
    <div>
      <label :for="`remove-vehicle-id-${_uid}`" class="block text-sm font-medium text-gray-700">Vehicle to Remove (Year/Make/Model or VIN) <span class="text-red-500">*</span></label>
      <input 
        type="text" 
        :id="`remove-vehicle-id-${_uid}`" 
        :value="modelValue.identifier" 
        @input="updateDetail('identifier', $event.target.value)"
        placeholder="e.g., 2020 Ford F-150 or VIN"
        class="input-responsive"
        required
        aria-describedby="remove-vehicle-id-error"
      />
      <p v-if="errors.identifier" id="remove-vehicle-id-error" class="error-message">{{ errors.identifier }}</p>
    </div>
    <div>
      <label :for="`remove-vehicle-date-${_uid}`" class="block text-sm font-medium text-gray-700">Date of Removal (if known)</label>
      <input 
        type="date" 
        :id="`remove-vehicle-date-${_uid}`" 
        :value="modelValue.removalDate" 
        @input="updateDetail('removalDate', $event.target.value)"
        class="input-responsive"
        aria-describedby="remove-vehicle-date-error"
      />
       <p v-if="errors.removalDate" id="remove-vehicle-date-error" class="error-message">{{ errors.removalDate }}</p>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, getCurrentInstance } from 'vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

// Get unique ID for labels/inputs
const instance = getCurrentInstance();
const _uid = instance ? instance.uid : Math.random().toString(36).substring(7);

const props = defineProps({
  modelValue: { 
    type: Object,
    required: true,
    default: () => ({ identifier: '', removalDate: '' }) 
  }
});

const emit = defineEmits(['update:modelValue']);

// --- Internal Validation Setup ---
const validationSchema = yup.object({
  identifier: yup.string().required('Vehicle identifier is required'),
  removalDate: yup.date().nullable().typeError('Invalid removal date'),
});

const { errors, validate: validateInternally, setErrors } = useForm({
  validationSchema: validationSchema,
  initialValues: props.modelValue 
});

const updateDetail = (key, value) => {
  const newValue = { 
    ...props.modelValue, 
    [key]: value 
  };
  emit('update:modelValue', newValue);
};

// --- Expose validation function to parent ---
defineExpose({
  validate() {
    let isValid = true;

    // Perform primary synchronous check for the required 'identifier' field
    if (!props.modelValue.identifier || !props.modelValue.identifier.trim()) {
      isValid = false;
    }

    // Trigger VeeValidate's internal validation.
    validateInternally({ force: true });

    // If our direct synchronous check already failed, return false.
    if (!isValid) {
      if (!errors.value.identifier) {
        setErrors({ identifier: 'Vehicle identifier is required' });
      }
      return false;
    }

    // After triggering VeeValidate and passing initial synchronous checks,
    // check the reactive 'errors' object.
    return Object.keys(errors.value).length === 0;
  }
});
</script>

<style scoped>
.input-responsive {
    @apply mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm;
}
.error-message {
    @apply text-red-500 text-xs italic mt-1;
}
</style> 